
import {
    setBreadcrumbLinks,
    setCurrentPageTitle,
} from '@/core/helpers/breadcrumb';
import {
    getErrorMsg,
    swalErrNotification,
    swalNotification,
} from '@/core/helpers/utils';
import { useButtonCustom } from '@/core/hooks/use-button-custom';
import { useDateTime } from '@/core/hooks/use-date-time-hook';
import { useLoading } from '@/core/hooks/use-loading';
import { usePagination } from '@/core/hooks/use-pagination';
import { useRouterCustom } from '@/core/hooks/use-router-custom';
import { useSafeRefetch } from '@/core/hooks/use-safe-refetch';
import {
    IBienBanGiaiTheResponseBase,
    IDinhMucCoDinhNewResponseBase,
    IKeHoachSuaChuaResponseBase,
} from '@/core/interfaces/ApiResponses';
import { BienBanGiaiTheService } from '@/core/services/BienBanGiaiThe.service';
import { KeHoachSuaChuaService } from '@/core/services/KeHoachSuaChua.service';
import { defineComponent } from 'vue';
import { DinhMucCoDinhService } from '@/core/services/DinhMucCoDinh.service';
import BaseLink from '@/components/shared/base-link/BaseLink.vue';
import ConfirmationDialog from '@/components/confirmation-dialog/ConfirmationDialog.vue';
import DinhMucCoDinhSection from '@/components/dinh-muc-co-dinh-section/DinhMucCoDinhSection.vue';
import BienBanGiaiTheTable from '@/components/shared/bien-ban-giai-the-table/BienBanGiaiTheTable.vue';
import ButtonCustom from '@/components/shared/buttons/ButtonCustom.vue';
import ConfirmHoanThanh from '@/components/shared/confirm-hoan-thanh-modal/ConfirmHoanThanh.vue';
import LabelCustom from '@/components/shared/label-custom/LabelCustom.vue';
import Pagination from '@/components/shared/pagination/Pagination.vue';
import PageContentBase from '@/layout/_shared/PageContentBase.vue';
import PageContentSection from '@/layout/_shared/PageContentSection.vue';
import ResponsiveLayout from '@/layout/_shared/ResponsiveLayout.vue';
import Can from '@/components/Can.vue';
import DetailTopicLottie from '@/components/shared/detail-topic-lottie/DetailTopicLottie.vue';
import {
  BIEN_BAN_GIAI_THE,
  KE_HOACH_SUA_CHUA,
} from '@/core/constants/permissions';
import { mapMutations } from 'vuex';

export default defineComponent({
  name: 'chi-tiet-ke-hoach-sua-chua',

  components: {
    BaseLink,
    BienBanGiaiTheTable,
    ButtonCustom,
    Can,
    ConfirmHoanThanh,
    ConfirmationDialog,
    DetailTopicLottie,
    DinhMucCoDinhSection,
    LabelCustom,
    PageContentBase,
    PageContentSection,
    Pagination,
    ResponsiveLayout,
  },

  setup() {
    const { goBack, push, id: idKeHoach } = useRouterCustom();
    const { ButtonTypeColors, ButtonsType } = useButtonCustom();
    const { startLoading, endLoading } = useLoading();
    const { currentPage, totalItems, perPage, perPageArr } = usePagination();
    const { moment } = useDateTime();

    return {
      BIEN_BAN_GIAI_THE,
      KE_HOACH_SUA_CHUA,
      goBack,
      push,
      idKeHoach,
      startLoading,
      endLoading,
      ButtonTypeColors,
      ButtonsType,
      currentPage,
      totalItems,
      perPage,
      perPageArr,
      moment,
    };
  },

  data() {
    return {
      bienBanItems: [] as IBienBanGiaiTheResponseBase[],
      confirmHoanThanhBienBan: false,
      dinhMucCoDinhhDetail: null as IDinhMucCoDinhNewResponseBase | null,
      isConfirmDeleteDialogDisplay: false,
      keHoachDetail: null as IKeHoachSuaChuaResponseBase | null,
      selectingBienBan: null as IBienBanGiaiTheResponseBase | null,
      selectedBienBanForDelete: null as IBienBanGiaiTheResponseBase | null,
      safeRefetchBienBan: null as (() => Promise<void>) | null,
    };
  },

  async mounted() {
    setBreadcrumbLinks([
      {
        text: 'Kế hoạch sửa chữa',
        routeData: { name: 'danh-sach-ke-hoach-sua-chua' },
      },
      {
        text: 'Chi tiết',
      },
    ]);

    await this.fetchData();

    // Initialize safe refetch after fetchBienBan method is available
    const { safeRefetch } = useSafeRefetch(() => this.fetchBienBan());
    this.safeRefetchBienBan = safeRefetch;

    this.fetchBienBan();

    setCurrentPageTitle(this.keHoachDetail?.ten_ke_hoach || 'Chi tiết kế hoạch sửa chữa');

    document.title = `${
      this.keHoachDetail?.ten_ke_hoach || 'Chi tiết'
    } - Kế hoạch sửa chữa - Đường sắt Hà Nội`;
  },

  watch: {
    async currentPage() {
      await this.fetchBienBan();
    },

    async perPage() {
      if (this.currentPage === 1) {
        await this.fetchBienBan();

        return;
      }

      this.currentPage = 1;
    },
  },

  methods: {
    ...mapMutations('ConfirmationDialogModule', {
      setDialog: 'setDialog',
      closeDialog: 'closeDialog',
    }),

    onDeleteKeHoachButtonClick() {
      if (!this.keHoachDetail) {
        return;
      }

      this.setDialog({
        message: `Bạn có chắc chắn muốn xoá kế hoạch sửa chữa ${this.keHoachDetail.ten_ke_hoach}?`,
        onConfirm: async () => {
          this.closeDialog();
          await this.startLoading();

          try {
            await KeHoachSuaChuaService.delete(this.keHoachDetail?.id ?? this.idKeHoach);
            await this.endLoading();
            await swalNotification('Xoá kế hoạch sửa chữa thành công');

            this.$router.replace({
              name: 'danh-sach-ke-hoach-sua-chua',
            });
          } catch (e) {
            await this.endLoading();
            await swalErrNotification(e, 'Không thể xoá kế hoạch sửa chữa này');
          }
        },
      });
    },

    async fetchData() {
      try {
        this.startLoading();

        const {
          data: { data },
        } = await KeHoachSuaChuaService.get(this.idKeHoach);

        this.keHoachDetail = data;

        this.dinhMucCoDinhhDetail =
          await DinhMucCoDinhService.getDinhMucCoDinhByIdCapSuaChuaAndDMTX({
            id_cap_sc: this.keHoachDetail.id_cap_sc,
            id_dm_toa_xe: this.keHoachDetail.id_danh_muc_toa_xe,
            status: 'valid',
          });

        this.endLoading();
      } catch (e) {
        this.endLoading();
        swalNotification(getErrorMsg(e, 'Có lỗi xảy ra'), 'error');
      }
    },

    async fetchBienBan() {
      try {
        this.startLoading();

        const {
          data: {
            data: { data, total },
          },
        } = await BienBanGiaiTheService.list({
          current_page: this.currentPage,
          per_page: this.perPage,
          id_ke_hoach_sc: this.idKeHoach,
        });

        this.bienBanItems = data;
        this.totalItems = total;

        this.endLoading();
      } catch (e) {
        this.endLoading();
        await swalNotification(getErrorMsg(e, 'Có lỗi xảy ra'), 'error');
      }
    },
    selectBienBanHandler({ id: selectedBienBanId }: { id: string }) {
      const foundBienBan = this.bienBanItems.find(
        ({ id: idBienBan }) => selectedBienBanId === idBienBan
      );

      if (foundBienBan) {
        this.selectingBienBan = foundBienBan;

        this.confirmHoanThanhBienBan = true;
      }
    },
    async hoanThanhHander() {
      this.confirmHoanThanhBienBan = false;

      await this.fetchBienBan();
    },
    onDeleteHandler({ item }: { item: IBienBanGiaiTheResponseBase }) {
      this.selectedBienBanForDelete = item;
      this.isConfirmDeleteDialogDisplay = true;
    },
    async confirmDelete() {
      if (!this.selectedBienBanForDelete) return;

      const bienBanToDelete = this.selectedBienBanForDelete;
      this.isConfirmDeleteDialogDisplay = false;
      this.selectedBienBanForDelete = null;

      try {
        await this.startLoading();

        await BienBanGiaiTheService.delete(bienBanToDelete.id);

        await swalNotification(
          `Xóa biên bản số ${bienBanToDelete.so_bien_ban} thành công`,
          'success'
        );

        await this.fetchBienBan();

        this.endLoading();
      } catch (e) {
        this.endLoading();
        await swalErrNotification(e, 'Có lỗi xảy ra khi xóa biên bản');
      }
    },
  },
});
